import {merge} from 'theme-ui'
import {tailwind} from '@theme-ui/presets'

const sharedButtonStyles = {
  border: 'none',
  color: 'white',
  cursor: 'pointer',
  fontFamily: 'body',
  fontWeight: 'bold',
  lineHeight: 1.2,
  transition: 'all 0.3s ease-in-out',
}
// export default merge(BaseTheme, {

export default merge(tailwind, {
  colors: {
    primary: '#ED8936',
    primaryMuted: '#fde3ce',
    lightorange: `#FBD38D`,
    // secondary: tailwind.colors.indigo[5],
    text: tailwind.colors.gray[8],
    heading: tailwind.colors.gray[9],
    subheading: tailwind.colors.gray[8],
    background: tailwind.colors.gray[1],
    black: `#141821`,
    divider: tailwind.colors.gray[8],
    textMuted: tailwind.colors.gray[5],
  },
  fonts: {
    body: 'Poppins, "IBM Plex Sans", -apple-system, BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
    heading:
      'Poppins, "IBM Plex Sans", -apple-system, BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
  },
  breakpoints: [`400px`, `600px`, `900px`, `1200px`, `1600px`],
  footer: {
    textAlign: `center`,
    display: `block`,
    position: `absolute`,
    bottom: 0,
    color: `textMuted`,
    px: [2, 3],
    py: [3, 4],
  },
  styles: {
    root: {
      strong: {
        fontSize: 2,
        letterSpacing: `-0.003em`,
        lineHeight: `body`,
        fontWeight: 'bold',
        '--baseline-multiplier': 0.179,
        '--x-height-multiplier': 0.35,
        color: `text`,
        my: 2,
      },
      h1: {
        lineHeight: `1.1`,
        fontSize: [4, 4, 6],
        fontWeight: '700',
        mt: 2,
        mb: 3,
        textShadow: `0px 6px 9px rgba(0, 0, 0, 0.12)`,
        letterSpacing: `wide`,
        color: `heading`,
      },
      h2: {
        fontSize: [4, 5],
        lineHeight: `1.1`,
        fontWeight: `normal`,
        mt: 2,
        mb: 2,
        color: 'subheading',
      },
      h3: {
        fontSize: [3, 4, 5],
        mt: 3,
        color: tailwind.colors.gray[8],
      },
      h4: {
        fontSize: [2, 3],
        letterSpacing: `small`,
        fontWeight: `bold`,
        mt: 4,
        mb: 1,
        color: tailwind.colors.gray[8],
      },
      h5: {
        fontSize: [2, 3],
        letterSpacing: `small`,
        fontWeight: `normal`,
        mt: 2,
        mb: 1,
        color: tailwind.colors.gray[8],
      },
      h6: {
        fontSize: 1,
        mb: 2,
        color: tailwind.colors.gray[8],
      },
      margin: 0,
      padding: 0,
      boxSizing: `border-box`,
      textRendering: `optimizeLegibility`,
      WebkitFontSmoothing: `antialiased`,
      MozOsxFontSmoothing: `grayscale`,
      color: `text`,
      backgroundColor: `background`,
      a: {
        color: `primary`,
        textDecoration: `none`,
        transition: `all 0.3s ease-in-out`,
        '&:hover': {
          color: `primary`,
          textDecoration: `none`,
        },
      },
      button: {
        fontFamily: `body`,
        fontSize: `1rem`,
      },
      p: {
        fontSize: 2,
        letterSpacing: `-0.003em`,
        lineHeight: `body`,
        '--baseline-multiplier': 0.179,
        '--x-height-multiplier': 0.35,
        color: `text`,
        my: 2,
        mb: '1.5rem',
      },
    },
    blockquote: {
      marginLeft: 0,
      p: {
        fontSize: [2, 3],
        fontWeight: `medium`,
        color: tailwind.colors.gray[8],
      },
    },
  },
  layout: {
    article: '46rem',
    base: '70rem',
    big: '86.66rem',
    separation: {
      desktop: '16rem',
      mobile: '6rem',
    },
  },
  borderRadius: {
    default: '2rem',
  },
  buttons: {
    base: {
      ...sharedButtonStyles,
      borderRadius: 'full',
      px: 4,
      py: 2,
    },
    primary: {
      ...sharedButtonStyles,
      boxShadow: 'button.default',
      fontSize: '1.5rem',
      backgroundColor: (t) => t.colors.primary,
      px: '1.5rem',
      py: '0.75rem',
      borderRadius: 'full',
      '&:hover': {
        boxShadow: 'button.hover',
        transform: 'translateY(-4px)',
      },
      svg: {
        height: '1.5rem ',
      },
    },
    secondary: {
      ...sharedButtonStyles,
      boxShadow: 'button.default',
      color: (t) => t.colors.primary,
      fontSize: '1.5rem',
      backgroundColor: 'transparent',
      px: '1.5rem',
      py: '0.5rem',
      border: (t) => `2px solid ${t.colors.lightorange}`,
      borderRadius: 'full',
      '&:hover': {
        boxShadow: 'button.hover',
        transform: 'translateY(-8px)',
      },
      svg: {
        height: '1.5rem ',
      },
    },
    toggle: {
      color: `background`,
      border: `none`,
      backgroundColor: `text`,
      cursor: `pointer`,
      alignSelf: `center`,

      ml: 3,
    },
  },
  texts: {
    bigger: {
      p: {
        fontSize: [2, 3, 4],
      },
    },
  },
  shadows: {
    button: {
      default: `0 23px 28px rgba(237, 137, 54, 0.2)`,
      hover: `0 26px 20px rgba(237, 137, 54, 0.15)`,
      dblack: `0 16px 28px rgba(0, 0, 0, 0.2)`,
    },
    card: '0 20px 30px rgba(0, 0, 0, 0.1)',
    image: '0 15px 25px rgba(0, 0, 0, 0.1)',
    feature: {
      big: {
        default: '0 40px 40px rgba(0, 0, 0, 0.15)',
        hover: '0 50px 50px rgba(0, 0, 0, 0.1)',
      },
      small: {
        default: '0 20px 30px rgba(0, 0, 0, 0.2)',
        hover: '0 40px 45px rgba(0, 0, 0, 0.1)',
      },
    },
    text: {
      small: '0 5px 10px rgba(0, 0, 0, 0.25)',
      big: '0 15px 20px rgba(0, 0, 0, 0.13)',
    },
  },
  transitions: {
    default: {
      transition: `all 0.4s cubic-bezier(0.645, 0.045, 0.355, 1)`,
      duration: `0.4s`,
    },
    boom: {
      transition: `all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)`,
    },
  },
  gradients: {
    primary: (t) =>
      `linear-gradient(45deg, ${t.colors.primary}, ${t.colors.indigo[6]})`,
  },
})
